import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Grid,
  Divider,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CompareIcon from '@mui/icons-material/Compare';
import PersonIcon from '@mui/icons-material/Person';
import ScoreboardIcon from '@mui/icons-material/Scoreboard';
import EventIcon from '@mui/icons-material/Event';
import DevicesIcon from '@mui/icons-material/Devices';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import { useNavigate, useParams } from 'react-router-dom';
import lifeDeclarationReviewService from '../services/lifeDeclarationReviewService';
import ActionButton from '../components/ActionButton';
import { useActionLock } from '../hooks/useActionLock';

// ─────────────────────────────────────────────────────────────
const REFERENCE_IMAGE_CONFIG = [
  { key: 'primary',      label: 'Primary',      badge: '🔵', desc: 'Enrollment reference' },
  { key: 'semi_primary', label: 'Semi-Primary',  badge: '🟠', desc: 'Most recent semi-primary' },
  { key: 'secondary_1', label: 'Secondary 1',   badge: '⚪', desc: 'Most recent secondary' },
  { key: 'secondary_2', label: 'Secondary 2',   badge: '⚪', desc: '2nd most recent secondary' },
];

const scoreChipColor = (score) => {
  const n = parseFloat(score);
  if (isNaN(n)) return { bg: '#F1F5F9', color: '#64748B' };
  if (n >= 80)  return { bg: '#DCFCE7', color: '#166534' };
  if (n >= 65)  return { bg: '#FEF9C3', color: '#854D0E' };
  return { bg: '#FEE2E2', color: '#991B1B' };
};

// ─────────────────────────────────────────────────────────────
// ReviewImage
// ─────────────────────────────────────────────────────────────
const ReviewImage = ({ label, badge, desc, blobUrl, loading, error, meta }) => (
  <Box
    sx={{
      border: '1px solid #E2E8F0',
      borderRadius: 2,
      overflow: 'hidden',
      backgroundColor: 'white',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    }}
  >
    {/* Header */}
    <Box
      sx={{
        px: 1.5, py: 1,
        background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
        borderBottom: '1px solid #BFDBFE',
        display: 'flex', alignItems: 'center', gap: 0.75,
      }}
    >
      <Typography sx={{ fontSize: '18px', lineHeight: 1 }}>{badge}</Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#1E293B', lineHeight: 1.3 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '11px', color: '#64748B', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {desc}
        </Typography>
      </Box>
      {meta?.confidence_score && (
        <Chip
          label={`${parseFloat(meta.confidence_score).toFixed(1)}%`}
          size="small"
          sx={{ fontSize: '11px', height: 20, fontWeight: 700, flexShrink: 0, ...scoreChipColor(meta.confidence_score) }}
        />
      )}
    </Box>

    {/* Image */}
    <Box
      sx={{
        flex: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: 200,
        backgroundColor: '#F8FAFC',
        overflow: 'hidden',
      }}
    >
      {loading ? (
        <CircularProgress size={36} sx={{ color: '#4E83BD' }} />
      ) : error || !blobUrl ? (
        <Box sx={{ textAlign: 'center', p: 3 }}>
          <ImageNotSupportedIcon sx={{ color: '#CBD5E1', fontSize: 48, mb: 1 }} />
          <Typography sx={{ fontSize: '12px', color: '#94A3B8' }}>{error || 'Not available'}</Typography>
        </Box>
      ) : (
        <img
          src={blobUrl}
          alt={label}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', maxHeight: 280 }}
        />
      )}
    </Box>

    {/* Footer */}
    {meta?.created_at && (
      <Box sx={{ px: 1.5, py: 0.75, borderTop: '1px solid #E2E8F0', backgroundColor: '#F8FAFC' }}>
        <Typography sx={{ fontSize: '11px', color: '#64748B' }}>
          📅 {new Date(meta.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
        </Typography>
      </Box>
    )}
  </Box>
);

// ─────────────────────────────────────────────────────────────
// Detail page
// ─────────────────────────────────────────────────────────────
const LifeDeclarationReviewDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [detail, setDetail]         = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError]   = useState(null);

  const [imageBlobUrls, setImageBlobUrls] = useState({});
  const [imageLoading, setImageLoading]   = useState({});
  const [imageErrors, setImageErrors]     = useState({});

  const [reviewNotes, setReviewNotes]         = useState('');
  const [pendingDecision, setPendingDecision] = useState(null);
  const [confirmOpen, setConfirmOpen]         = useState(false);
  const [submitting, runSubmit]               = useActionLock();

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const showSnackbar = (msg, sev = 'success') => setSnackbar({ open: true, message: msg, severity: sev });

  // ── Load ──────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setPageLoading(true);
      setPageError(null);

      const result = await lifeDeclarationReviewService.getReview(id);
      if (cancelled) return;

      if (!result.success) { setPageError(result.error); setPageLoading(false); return; }

      setDetail(result.data);
      setPageLoading(false);

      const fetchImg = async (imageKey) => {
        setImageLoading((p) => ({ ...p, [imageKey]: true }));
        const r = await lifeDeclarationReviewService.getImageBlob(id, imageKey);
        if (cancelled) return;
        if (r.success) setImageBlobUrls((p) => ({ ...p, [imageKey]: r.data.blobUrl }));
        else           setImageErrors((p)   => ({ ...p, [imageKey]: r.error }));
        setImageLoading((p) => ({ ...p, [imageKey]: false }));
      };

      if (result.data.submitted_selfie?.url) fetchImg('submitted_selfie');
      REFERENCE_IMAGE_CONFIG.forEach(({ key }) => {
        if (result.data.reference_images?.[key]) fetchImg(key);
      });
    };

    load();
    return () => {
      cancelled = true;
      setImageBlobUrls((prev) => {
        Object.values(prev).forEach((u) => { if (u?.startsWith('blob:')) URL.revokeObjectURL(u); });
        return {};
      });
    };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Decision ──────────────────────────────────────
  const handleDecisionClick = (decision) => { setPendingDecision(decision); setConfirmOpen(true); };

  const handleConfirmSubmit = () => {
    setConfirmOpen(false);
    runSubmit(async () => {
      const result = await lifeDeclarationReviewService.submitReview(id, {
        decision: pendingDecision,
        ...(reviewNotes.trim() ? { notes: reviewNotes.trim() } : {}),
      });
      if (result.success) {
        showSnackbar(
          pendingDecision === 'approved' ? 'Declaration approved successfully.' : 'Declaration rejected.',
          'success'
        );
        setTimeout(() => navigate('/life-declaration-review'), 1500);
      } else {
        showSnackbar(result.error, 'error');
      }
    });
  };

  // ── States ────────────────────────────────────────
  if (pageLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#4E83BD' }} />
      </Box>
    );
  }
  if (pageError) {
    return (
      <Box sx={{ padding: { xs: 2, md: 3 } }}>
        <Alert severity="error" sx={{ mb: 2 }}>{pageError}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/life-declaration-review')}>Back to list</Button>
      </Box>
    );
  }

  const { bg: scoreBg, color: scoreColor } = scoreChipColor(detail.comparison_score);

  // ── Render ────────────────────────────────────────
  return (
    <Box sx={{ padding: { xs: 2, sm: 2.5, md: 3 }, backgroundColor: '#F8FAFC', minHeight: '100vh' }}>

      {/* ── Page Header ─────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          padding: { xs: 1.5, sm: 2, md: 2.5 },
          mb: 3,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton onClick={() => navigate('/life-declaration-review')} sx={{ color: 'white', p: 0.75 }}>
            <ArrowBackIcon />
          </IconButton>
          <CompareIcon sx={{ color: 'white', fontSize: 26 }} />
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: { xs: '15px', sm: '18px' }, color: 'white', lineHeight: 1.2 }}>
              Face Verification Review
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '12px' }}>
              {detail.user_name} · {detail.user_email}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* ── User Meta Card ──────────────────────────────── */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #E2E8F0', backgroundColor: 'white', mb: 3, overflow: 'hidden' }}>
        <Box sx={{ px: 2.5, py: 1.5, backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#1E293B' }}>Declaration Info</Typography>
        </Box>
        <Box sx={{ p: 2.5 }}>
          <Grid container spacing={3}>

            {/* User */}
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: '#DBEAFE', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <PersonIcon sx={{ color: '#3B82F6', fontSize: 22 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '11px', color: '#94A3B8', mb: 0.25 }}>User</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#1E293B' }}>{detail.user_name || '—'}</Typography>
                  <Typography sx={{ fontSize: '12px', color: '#64748B' }}>{detail.user_email}</Typography>
                </Box>
              </Box>
            </Grid>

            {/* Score */}
            <Grid item xs={6} sm={3} md={2}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: scoreBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <ScoreboardIcon sx={{ color: scoreColor, fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '11px', color: '#94A3B8', mb: 0.5 }}>Comparison Score</Typography>
                  <Chip label={`${parseFloat(detail.comparison_score || 0).toFixed(2)}%`} size="small" sx={{ backgroundColor: scoreBg, color: scoreColor, fontWeight: 700, fontSize: '13px' }} />
                </Box>
              </Box>
            </Grid>

            {/* Method */}
            <Grid item xs={6} sm={3} md={2}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <DevicesIcon sx={{ color: '#16A34A', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '11px', color: '#94A3B8', mb: 0.5 }}>Method</Typography>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1E293B', textTransform: 'capitalize' }}>
                    {detail.submission_method?.replace(/_/g, ' ') || '—'}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Submitted At */}
            <Grid item xs={6} sm={3} md={2}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: '#FEF9C3', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <EventIcon sx={{ color: '#CA8A04', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '11px', color: '#94A3B8', mb: 0.5 }}>Submitted At</Typography>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1E293B' }}>
                    {detail.declaration_timestamp
                      ? new Date(detail.declaration_timestamp).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                      : '—'}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Available */}
            <Grid item xs={6} sm={3} md={2}>
              <Box>
                <Typography sx={{ fontSize: '11px', color: '#94A3B8', mb: 0.5 }}>Reference Images</Typography>
                <Chip
                  label={`${detail.reference_images_available ?? 0} / 4 available`}
                  size="small"
                  sx={{ backgroundColor: '#DBEAFE', color: '#1D4ED8', fontWeight: 600, fontSize: '12px' }}
                />
              </Box>
            </Grid>

          </Grid>
        </Box>
      </Paper>

      {/* ── Reference Images ────────────────────────────── */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #E2E8F0', backgroundColor: 'white', mb: 3, overflow: 'hidden' }}>
        <Box sx={{ px: 2.5, py: 1.5, backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#1E293B' }}>Reference Images</Typography>
          <Typography sx={{ fontSize: '12px', color: '#64748B' }}>(used for comparison)</Typography>
        </Box>
        <Box sx={{ p: 2.5 }}>
          <Grid container spacing={2}>
            {REFERENCE_IMAGE_CONFIG.map(({ key, label, badge, desc }) => {
              const meta = detail.reference_images?.[key] || null;
              return (
                <Grid item xs={6} sm={3} key={key}>
                  <Box sx={{ height: 300 }}>
                    <ReviewImage
                      label={label}
                      badge={badge}
                      desc={desc}
                      blobUrl={imageBlobUrls[key]}
                      loading={!!imageLoading[key]}
                      error={!meta ? 'Not available' : imageErrors[key]}
                      meta={meta}
                    />
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Paper>

      {/* ── Submitted Selfie ────────────────────────────── */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #E2E8F0', backgroundColor: 'white', mb: 3, overflow: 'hidden' }}>
        <Box sx={{ px: 2.5, py: 1.5, backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#1E293B' }}>Submitted Selfie</Typography>
          <Typography sx={{ fontSize: '12px', color: '#64748B' }}>(image under review)</Typography>
        </Box>
        <Box sx={{ p: 2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={5} md={3}>
              <Box sx={{ height: 320 }}>
                <ReviewImage
                  label="Submitted Selfie"
                  badge="📸"
                  desc="Image under review"
                  blobUrl={imageBlobUrls['submitted_selfie']}
                  loading={!!imageLoading['submitted_selfie']}
                  error={imageErrors['submitted_selfie']}
                  meta={
                    detail.submitted_selfie
                      ? { created_at: detail.submitted_selfie.captured_at, confidence_score: detail.submitted_selfie.comparison_score }
                      : null
                  }
                />
              </Box>
            </Grid>

          </Grid>
        </Box>
      </Paper>

      {/* ── Review Notes + Actions ──────────────────────── */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #E2E8F0', backgroundColor: 'white', overflow: 'hidden' }}>
        <Box sx={{ px: 2.5, py: 1.5, backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#1E293B' }}>Review Decision</Typography>
        </Box>
        <Box sx={{ p: 2.5 }}>
          <Typography sx={{ fontSize: '13px', color: '#64748B', mb: 1 }}>
            Review Notes <Typography component="span" sx={{ fontSize: '12px', color: '#94A3B8' }}>(optional)</Typography>
          </Typography>
          <TextField
            multiline
            rows={3}
            fullWidth
            placeholder="Add notes about your decision…"
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#F8FAFC',
                '& fieldset': { borderColor: '#E2E8F0' },
                '&:hover fieldset': { borderColor: '#4E83BD' },
                '&.Mui-focused fieldset': { borderColor: '#4E83BD' },
              },
            }}
          />

          <Divider sx={{ mb: 2.5 }} />

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/life-declaration-review')}
              sx={{
                textTransform: 'none', fontWeight: 600, fontSize: '14px',
                borderRadius: '10px', px: 3, py: 1.2,
                color: '#64748B', borderColor: '#CBD5E1',
                '&:hover': { borderColor: '#94A3B8', backgroundColor: '#F8FAFC' },
              }}
            >
              Back to List
            </Button>

            <Box sx={{ flex: 1 }} />

            <ActionButton
              variant="outlined"
              startIcon={<CancelIcon />}
              loading={submitting && pendingDecision === 'rejected'}
              loadingText="Rejecting…"
              onClick={() => handleDecisionClick('rejected')}
              sx={{
                textTransform: 'none', fontWeight: 600, fontSize: '14px',
                borderRadius: '10px', px: 4, py: 1.2,
                color: '#DC2626', borderColor: '#DC2626',
                '&:hover': { backgroundColor: '#FEE2E2', borderColor: '#B91C1C' },
              }}
            >
              Reject
            </ActionButton>

            <ActionButton
              variant="contained"
              startIcon={<CheckCircleIcon />}
              loading={submitting && pendingDecision === 'approved'}
              loadingText="Approving…"
              onClick={() => handleDecisionClick('approved')}
              sx={{
                textTransform: 'none', fontWeight: 700, fontSize: '14px',
                borderRadius: '10px', px: 4, py: 1.2,
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                boxShadow: '0 4px 12px rgba(5,150,105,0.3)',
                '&:hover': { background: 'linear-gradient(135deg, #047857 0%, #065F46 100%)' },
              }}
            >
              Approve
            </ActionButton>
          </Box>
        </Box>
      </Paper>

      {/* ══ Confirm Dialog ══════════════════════════════════ */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <Box
          sx={{
            background: pendingDecision === 'approved'
              ? 'linear-gradient(135deg, #059669 0%, #047857 100%)'
              : 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)',
            px: 3, py: 2.5,
            display: 'flex', alignItems: 'center', gap: 1.5,
          }}
        >
          {pendingDecision === 'approved'
            ? <CheckCircleIcon sx={{ color: 'white', fontSize: 28 }} />
            : <CancelIcon sx={{ color: 'white', fontSize: 28 }} />}
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '16px', color: 'white' }}>
              {pendingDecision === 'approved' ? 'Approve Declaration?' : 'Reject Declaration?'}
            </Typography>
            <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>
              This action will update the review status
            </Typography>
          </Box>
        </Box>

        <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <Typography sx={{ color: '#475569', fontSize: '14px', lineHeight: 1.7 }}>
            {pendingDecision === 'approved'
              ? 'You are confirming that the submitted selfie matches the reference images. The declaration will be verified and the schedule will advance.'
              : 'You are rejecting this declaration. The schedule will reset to PENDING so the user can retry.'}
          </Typography>
          {reviewNotes.trim() && (
            <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1.5, backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
              <Typography sx={{ fontSize: '11px', color: '#64748B', fontWeight: 600, mb: 0.5 }}>Review notes:</Typography>
              <Typography sx={{ fontSize: '12px', color: '#475569' }}>{reviewNotes}</Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1.5 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            variant="outlined"
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', color: '#475569', borderColor: '#CBD5E1' }}
          >
            Back
          </Button>
          <ActionButton
            variant="contained"
            loading={submitting}
            loadingText={pendingDecision === 'approved' ? 'Approving…' : 'Rejecting…'}
            onClick={handleConfirmSubmit}
            sx={{
              textTransform: 'none', fontWeight: 600, borderRadius: '8px',
              background: pendingDecision === 'approved'
                ? 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                : 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)',
            }}
          >
            Confirm {pendingDecision === 'approved' ? 'Approve' : 'Reject'}
          </ActionButton>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ──────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LifeDeclarationReviewDetail;
